import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Typography,
} from "@mui/material";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import SeoHead from "../components/SeoHead";
import RosterSlotGrid from "../components/community/RosterSlotGrid";
import VoteBar from "../components/community/VoteBar";
import {
  useGetCommunityMatchupQuery,
  useVoteCommunityMatchupMutation,
} from "../store/api/communityApi";
import { useAuth } from "../contexts/AuthContext";
import { useAnalytics } from "../hooks/useAnalytics";
import { brandFilledButtonSx, brandOutlinedButtonSx } from "../utils/brandAssets";
import {
  communityBuildPath,
  filledPlayerNames,
  publicMatchupPath,
} from "../utils/community";
import { colors } from "../theme";

const Side = ({ roster, sport, selected, onVote, canVote }) => (
  <Box
    sx={{
      border: `1px solid ${selected ? "rgba(132, 230, 229, 0.5)" : colors.border}`,
      borderRadius: 2,
      p: 2,
    }}
  >
    <Typography variant="overline" color="text.secondary">
      {roster?.owner?.username || "Fan"}
    </Typography>
    <Typography variant="h5" fontWeight={800} sx={{ mb: 2 }}>
      {roster?.title}
    </Typography>
    <RosterSlotGrid roster={roster} sport={sport} compact />
    {canVote && (
      <Button
        fullWidth
        variant={selected ? "contained" : "outlined"}
        sx={{ mt: 2, ...(selected ? brandFilledButtonSx : brandOutlinedButtonSx) }}
        onClick={onVote}
      >
        {selected ? `Voted ${roster?.title}` : `Vote ${roster?.title}`}
      </Button>
    )}
  </Box>
);

const CommunityMatchup = ({ publicView = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { data: matchup, isLoading, error } = useGetCommunityMatchupQuery(id, {
    skip: !id,
  });
  const [vote, { isLoading: voting }] = useVoteCommunityMatchupMutation();
  const { trackCustomEvent } = useAnalytics();
  const [notice, setNotice] = React.useState("");

  const handleVote = async (choiceId) => {
    if (!isAuthenticated) {
      navigate(`/login?next=${encodeURIComponent(window.location.pathname)}`);
      return;
    }
    try {
      await vote({ id, choiceId }).unwrap();
      trackCustomEvent("matchup_voted", { matchup_id: id });
    } catch (err) {
      setNotice(err?.data?.error || "Could not record your vote");
    }
  };

  const copyLink = async () => {
    const path = publicMatchupPath(matchup);
    if (!path) return;
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${path}`);
      setNotice("Link copied");
      trackCustomEvent("community_object_shared", { type: "matchup" });
    } catch (_err) {
      setNotice("Could not copy link");
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !matchup) {
    return <Alert severity="info">Matchup not found.</Alert>;
  }

  const preview = [
    filledPlayerNames(matchup.rosterA, 5).join(" · "),
    filledPlayerNames(matchup.rosterB, 5).join(" · "),
  ]
    .filter(Boolean)
    .join(" VS ");

  return (
    <Box sx={{ py: publicView ? 0 : { xs: 1, sm: 2 }, maxWidth: 1280, mx: "auto" }}>
      <SeoHead
        title={`${matchup.rosterA?.title} vs ${matchup.rosterB?.title} — Stats.Fan`}
        description={`${preview}. ${matchup.totalVotes} community votes.`}
      />
      <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: "0.16em" }}>
        Head-to-head
      </Typography>
      <Typography variant="h3" component="h1" fontWeight={800} sx={{ mt: 0.5 }}>
        {matchup.rosterA?.title} vs {matchup.rosterB?.title}
      </Typography>
      <Box sx={{ mt: 3, mb: 4 }}>
        <VoteBar matchup={matchup} />
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr auto 1fr" },
          gap: 2,
          alignItems: "start",
        }}
      >
        <Side
          roster={matchup.rosterA}
          sport={matchup.sport}
          selected={matchup.viewerChoiceId === matchup.rosterA?.id}
          canVote={!voting}
          onVote={() => handleVote(matchup.rosterA.id)}
        />
        <Typography
          variant="h6"
          fontWeight={800}
          sx={{ textAlign: "center", alignSelf: "center" }}
        >
          VS
        </Typography>
        <Side
          roster={matchup.rosterB}
          sport={matchup.sport}
          selected={matchup.viewerChoiceId === matchup.rosterB?.id}
          canVote={!voting}
          onVote={() => handleVote(matchup.rosterB.id)}
        />
      </Box>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 4 }}>
        <Button
          component={RouterLink}
          to={communityBuildPath(matchup.sport, {
            versus: matchup.rosterA?.id,
          })}
          variant="contained"
          sx={brandFilledButtonSx}
        >
          Build yours
        </Button>
        <Button variant="outlined" onClick={copyLink}>
          Share
        </Button>
      </Box>
      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={3000}
        onClose={() => setNotice("")}
        message={notice}
      />
    </Box>
  );
};

export default CommunityMatchup;
